import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import client from 'client';
import { showMessage } from 'app/store/fuse/messageSlice';
import { formatter } from '../../../constants';

function formatQty(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatMoney(value) {
  const number = Number(value);
  return formatter.format(Number.isFinite(number) ? number : 0);
}

function csvFilenameFromResponse(response, fallback) {
  const header = response?.headers?.['content-disposition'] || '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
  const raw = match?.[1] || match?.[2];
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch (error) {
    return raw;
  }
}

async function saveBlobWithDialog(blob, filename) {
  if (typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'CSV', accept: { 'text/csv': ['.csv'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') return false;
    }
  }
  saveAs(blob, filename);
  return true;
}

const headerCell = {
  fontWeight: 700,
  fontSize: 15,
  border: 0,
  color: '#111',
  background: '#e7cc0b',
  whiteSpace: 'nowrap',
};

const totalCell = {
  fontWeight: 700,
  fontSize: 15,
  border: 0,
  color: '#fff',
  background: '#222',
};

const DailyUnitKpiDialog = ({ open, onClose, jobId, compacctnum, unitName, dispatchDate }) => {
  const { t } = useTranslation('navigation');
  const dispatch = useDispatch();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [newestFirst, setNewestFirst] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  const notify = (message, color) => {
    dispatch(
      showMessage({
        message: <Typography>{message}</Typography>,
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: color,
      })
    );
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !jobId || !compacctnum) return;
      setLoading(true);
      setFailed(false);
      try {
        const result = await client.post(`/jobs/daily-unit-kpi/${jobId}`, { compacctnum });
        if (!cancelled) setReport(result?.data || null);
      } catch (error) {
        if (!cancelled) {
          setReport(null);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, jobId, compacctnum]);

  useEffect(() => {
    if (!open) {
      setNewestFirst(true);
      setExpanded({});
      return;
    }
    if (dispatchDate) setExpanded({ [String(dispatchDate).slice(0, 10)]: true });
  }, [open, dispatchDate]);

  const days = [...(report?.days || [])]
    .filter((day) => !dispatchDate || String(day.date).slice(0, 10) === String(dispatchDate).slice(0, 10))
    .sort((a, b) => (newestFirst ? (a.date < b.date ? 1 : -1) : a.date > b.date ? 1 : -1));
  const showFinancials = Boolean(report?.include_financials);
  const showMaterial = showFinancials && Boolean(report?.show_material);
  const titleUnit = unitName || report?.unit_name || t('UNIT', { defaultValue: 'Unit' });
  const totals = days.reduce(
    (sum, day) => ({
      unit_qty: sum.unit_qty + (Number(day.unit_qty) || 0),
      ticket_count: sum.ticket_count + (Number(day.ticket_count) || 0),
      hauling_invoice: sum.hauling_invoice + (Number(day.hauling_invoice) || 0),
      hauling_cost: sum.hauling_cost + (Number(day.hauling_cost) || 0),
      material_value: sum.material_value + (Number(day.material_value) || 0),
    }),
    { unit_qty: 0, ticket_count: 0, hauling_invoice: 0, hauling_cost: 0, material_value: 0 }
  );

  const requestDailyTonsCsv = async () => {
    if (!compacctnum || jobId == null || csvLoading) return;
    setCsvLoading(true);
    try {
      const response = await client.post(
        `/jobs/daily-tons/${jobId}`,
        { compacctnum },
        { responseType: 'blob' }
      );
      const blob = response?.data;
      if (!blob || response.status === 204 || blob.size === 0) {
        notify(t('NO_INFORMATION_WAS_FOUND_MESSAGE'), 'error');
        return;
      }
      if (String(blob.type || '').includes('json')) {
        notify(t('NO_INFORMATION_WAS_FOUND_MESSAGE'), 'error');
        return;
      }
      const filename = csvFilenameFromResponse(response, `daily-tons-${jobId}.csv`);
      setCsvFile({ blob, filename });
      setCsvOpen(true);
    } catch (error) {
      notify(t('SOMETHING_WENT_WRONG'), 'error');
    } finally {
      setCsvLoading(false);
    }
  };

  const downloadDailyTonsCsv = async () => {
    if (!csvFile?.blob) return;
    const saved = await saveBlobWithDialog(csvFile.blob, csvFile.filename);
    if (saved) {
      setCsvOpen(false);
      setCsvFile(null);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg' className='common-modal'>
      <DialogTitle
        align='center'
        sx={{ fontWeight: 600, fontSize: 22, textTransform: 'uppercase', pr: 12 }}
      >
        {t('DAILY_UNIT_TITLE', {
          unit: titleUnit,
          defaultValue: 'Daily {{unit}}',
        })}
      </DialogTitle>
      <Tooltip title='Export CSV'>
        <IconButton
          aria-label='Export CSV'
          disabled={csvLoading}
          onClick={requestDailyTonsCsv}
          sx={{ position: 'absolute', right: 52, top: 12 }}
        >
          <svg xmlns='http://www.w3.org/2000/svg' height='32' viewBox='0 -960 960 960' width='32'>
            <path d='M230-360h120v-60H250v-120h100v-60H230q-17 0-28.5 11.5T190-560v160q0 17 11.5 28.5T230-360Zm156 0h120q17 0 28.5-11.5T546-400v-60q0-17-11.5-31.5T506-506h-60v-34h100v-60H426q-17 0-28.5 11.5T386-560v60q0 17 11.5 30.5T426-456h60v36H386v60Zm264 0h60l70-240h-60l-40 138-40-138h-60l70 240ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z' />
          </svg>
        </IconButton>
      </Tooltip>
      <div className='popup-close'>
        <CloseIcon onClick={onClose} />
      </div>
      <DialogContent>
        {loading ? (
          <div className='flex justify-center py-24'>
            <CircularProgress />
          </div>
        ) : failed ? (
          <Typography>{t('AN_ERROR_OCCURED', { defaultValue: 'An error occured' })}</Typography>
        ) : days.length === 0 ? (
          <Typography>
            {dispatchDate
              ? t('NO_ACTIVITY_THIS_DISPATCH_DAY', { defaultValue: 'No activity for this dispatch day' })
              : t('NO_DAILY_ACTIVITY', { defaultValue: 'No daily activity for this job yet' })}
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 'none', border: '1px solid #ccc6', borderRadius: 0 }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ ...headerCell, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setNewestFirst((current) => !current)}
                  >
                    {t('DATES', { defaultValue: 'Dates' })} {newestFirst ? '↓' : '↑'}
                  </TableCell>
                  <TableCell align='right' sx={headerCell}>
                    {t('TOTAL_UNIT_VALUE', { defaultValue: 'Total Unit Value' })}
                  </TableCell>
                  <TableCell align='right' sx={headerCell}>
                    {t('TOTAL_TICKETS', { defaultValue: 'Total Tickets' })}
                  </TableCell>
                  {showFinancials ? (
                    <TableCell align='right' sx={headerCell}>
                      {t('TOTAL_HAULING_INVOICE_VALUE', { defaultValue: 'Total Hauling Invoice Value' })}
                    </TableCell>
                  ) : null}
                  {showFinancials ? (
                    <TableCell align='right' sx={headerCell}>
                      {t('TOTAL_HAULING_COST_VALUE', { defaultValue: 'Total Hauling Cost Value' })}
                    </TableCell>
                  ) : null}
                  {showMaterial ? (
                    <TableCell align='right' sx={headerCell}>
                      {t('TOTAL_MATERIAL_VALUE', { defaultValue: 'Total Material Value' })}
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {days.map((day, index) => {
                  const isOpen = Boolean(expanded[day.date]);
                  const rowColor = index % 2 === 0 ? '#fff' : '#f7f7f7';
                  return (
                    <React.Fragment key={day.date}>
                      <TableRow
                        sx={{ background: rowColor, cursor: 'pointer' }}
                        onClick={() =>
                          setExpanded((current) => ({ ...current, [day.date]: !current[day.date] }))
                        }
                      >
                        <TableCell>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {isOpen ? (
                              <KeyboardArrowDownIcon fontSize='small' />
                            ) : (
                              <KeyboardArrowRightIcon fontSize='small' />
                            )}
                            {moment(day.date, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                          </span>
                        </TableCell>
                        <TableCell align='right'>{formatQty(day.unit_qty)}</TableCell>
                        <TableCell align='right'>{day.ticket_count}</TableCell>
                        {showFinancials ? (
                          <TableCell align='right'>{formatMoney(day.hauling_invoice)}</TableCell>
                        ) : null}
                        {showFinancials ? (
                          <TableCell align='right'>{formatMoney(day.hauling_cost)}</TableCell>
                        ) : null}
                        {showMaterial ? (
                          <TableCell align='right'>{formatMoney(day.material_value)}</TableCell>
                        ) : null}
                      </TableRow>
                      {isOpen
                        ? (day.tickets || []).map((ticket) => (
                            <TableRow key={ticket.tk_id} sx={{ background: '#f3f3f3' }}>
                              <TableCell sx={{ pl: 5, fontSize: 13 }}>
                                {t('TICKET_NO', { defaultValue: 'Ticket No.' })} {ticket.tk_no}
                                {ticket.driver_name ? ` · ${ticket.driver_name}` : ''}
                                {Number(ticket.tk_status) === 0
                                  ? ` · ${t('IN_PROGRESS', { defaultValue: 'In Progress' })}`
                                  : ''}
                              </TableCell>
                              <TableCell align='right' sx={{ fontSize: 13 }}>
                                {formatQty(ticket.unit_qty)}
                              </TableCell>
                              <TableCell />
                              {showFinancials ? (
                                <TableCell align='right' sx={{ fontSize: 13 }}>
                                  {formatMoney(ticket.hauling_invoice)}
                                </TableCell>
                              ) : null}
                              {showFinancials ? (
                                <TableCell align='right' sx={{ fontSize: 13 }}>
                                  {formatMoney(ticket.hauling_cost)}
                                </TableCell>
                              ) : null}
                              {showMaterial ? (
                                <TableCell align='right' sx={{ fontSize: 13 }}>
                                  {formatMoney(ticket.material_value)}
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))
                        : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell sx={totalCell}>{t('GRAND_TOTAL', { defaultValue: 'Grand Total' })}</TableCell>
                  <TableCell align='right' sx={totalCell}>
                    {formatQty(totals.unit_qty)}
                  </TableCell>
                  <TableCell align='right' sx={totalCell}>
                    {totals.ticket_count}
                  </TableCell>
                  {showFinancials ? (
                    <TableCell align='right' sx={totalCell}>
                      {formatMoney(totals.hauling_invoice)}
                    </TableCell>
                  ) : null}
                  {showFinancials ? (
                    <TableCell align='right' sx={totalCell}>
                      {formatMoney(totals.hauling_cost)}
                    </TableCell>
                  ) : null}
                  {showMaterial ? (
                    <TableCell align='right' sx={totalCell}>
                      {formatMoney(totals.material_value)}
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
    <Dialog open={csvOpen} onClose={() => setCsvOpen(false)} maxWidth='xs' fullWidth>
      <DialogTitle>{t('EXPORT_CSV')}</DialogTitle>
      <DialogContent>
        <Typography>{csvFile?.filename}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCsvOpen(false)}>{t('CANCEL')}</Button>
        <Button variant='contained' onClick={downloadDailyTonsCsv}>
          {t('DOWNLOAD')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default DailyUnitKpiDialog;
